import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MetricSharedLinkService } from '../../service/backend/metric-shared-link-service';
import { firstValueFrom } from 'rxjs';
import {
  MetricGroupsAndPeriods,
  MetricsForExternalUsers,
  MetricUploadByExternalUser,
} from '../../dto/MetricGroupsAndPeriods';
import { getAllRequestedMetricData, MetricGroup } from '../../dto/MetricGroup';
import { MetricSharedLinkRelatedInfo } from '../../dto/MetricSharedLinkWrapper';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { MetricDataRole } from '../../dto/MetricData';
import { MetricGroupFormWrapper } from '../../dto/MetricGroupForm';
import { MetricFillService } from '../../service/internal/metric-fill.service';
import { MetricBulkUploadParent } from '../../dto/MetricBulkUploadDTO';
import {
  ExternalCompanyMetricsShow,
  ExternalCompanyMetricsState,
  ExternalCompanyMetricsStateWrapper,
  ExternalCompanyMetricsTabs,
  ExternalCompanyMetricsTabsWrapper,
} from '../../dto/ExternalCompanyMetrics';
import { ButtonTooltip } from 'src/app/domain/shared/component/buttons/button.component';

@Component({
  selector: 'app-external-company-metrics',
  templateUrl: './external-company-metrics.component.html',
  styleUrls: ['./external-company-metrics.component.scss'],
})
export class ExternalCompanyMetricsComponent implements OnInit {
  password: string;
  readonly metricDataRole = MetricDataRole;
  readonly metricLocation = MetricBulkUploadParent.EXTERNAL;
  readonly Tabs = ExternalCompanyMetricsTabs;


  token: string; // auth token to speak with the backend. Determines the org + company.
  relatedInfo: MetricSharedLinkRelatedInfo;
  submissionForm: FormGroup<{
    name: FormControl<string>;
    email: FormControl<string>;
  }>;

  show: ExternalCompanyMetricsShow;
  groupsWithAllPeriods: MetricsForExternalUsers;
  contactPersonTooltip: ButtonTooltip;
  form: MetricGroupFormWrapper;

  tabs = new ExternalCompanyMetricsTabsWrapper();

  state = new ExternalCompanyMetricsStateWrapper();

  constructor(
    private mslService: MetricSharedLinkService,
    private notificationService: NotificationsService,
  ) {
  }

  ngOnInit(): void {
    this.initVariables();
    this.proceedToken();
  }

  setNextTab(): void {
    const selectedIndexTab = this.tabs.all.indexOf(this.tabs.selected);
    const nextIndexTab = selectedIndexTab + 1;
    const nextTab = this.tabs.all[nextIndexTab];
    this.tabs.selected = nextTab;
    this.tabs.isActualSelected = nextTab === this.Tabs.Actual;
  }

  setPreviousTab(): void {
    const selectedIndexTab = this.tabs.all.indexOf(this.tabs.selected);
    const previousIndexTab = selectedIndexTab - 1;
    const previousTab = this.tabs.all[previousIndexTab];
    this.tabs.selected = previousTab;
    this.tabs.isActualSelected = previousTab === this.Tabs.Actual;
  }

  async checkPassword(password: string, captchaV2Token: string, captchaV3Token: string): Promise<boolean> {
    this.groupsWithAllPeriods = await firstValueFrom(this.mslService.getByPassword(
      {
        token: this.token,
        password: password,
        captchaV2Token: captchaV2Token,
        captchaV3Token: captchaV3Token,
      }));
    return true;
  }

  goToLogin(): void {
    window.location.replace(`${document.location.origin}/login`);
  }

  sendEmail(): void {
    window.open(`mailto:${this.groupsWithAllPeriods.contact.email}?subject=KPI metrics help request`);
  }

  save(): void {
    const rawWrapper = {
      metricGroups: this.form.value.groups as MetricGroup[],
      periods: this.groupsWithAllPeriods.periods,
    } as MetricGroupsAndPeriods;
    const metricGroupsAndPeriods = MetricGroupsAndPeriods.deserializeMetricGroupsAndPeriods(rawWrapper);
    const wrapper: MetricUploadByExternalUser = {
      request: {
        token: this.token,
        password: this.password,
      },
      metricData: getAllRequestedMetricData(metricGroupsAndPeriods),
      userData: this.submissionForm.getRawValue(),
    };
    this.mslService.saveRequestedData(this.relatedInfo.company.uuid, wrapper).subscribe(
      {
        next: () => {
          this.show.saveDialog = false;
          this.show.modalAfterSuccessfulSubmission = true;
          this.notificationService.success(NOTIFICATIONS.METRICS_UPDATED_SUCCESS);
        },
        error: () => {
          this.notificationService.error(NOTIFICATIONS.METRICS_UPDATED_ERROR);
        },
      });
  }

  afterBulkUpload(updatedGroups: MetricGroupsAndPeriods): void {
    if (!updatedGroups) {
      this.show.modalBulkUpload = false;
      return;
    }

    this.setDataFromBulkUpload(updatedGroups);
    this.show.modalBulkUpload = false;
  }

  proceedWithFetchedData(password: string): void {
    this.password = password;
    this.updateForm();
    this.setTabs();
    this.state.current = ExternalCompanyMetricsState.AUTHORIZED;
    this.contactPersonTooltip = {
      showCondition: false,
      text: 'Contact person',
      id: 'preview',
    };
  }

  private initVariables(): void {
    this.show = {
      saveDialog: false,
      modalBulkUpload: false,
      modalAfterSuccessfulSubmission: false,
      captchaV2: false,
    };
    this.submissionForm = new FormGroup({
      name: new FormControl<string>('', [Validators.required]),
      email: new FormControl<string>('', [
        Validators.required,
        Validators.email,
      ]),
    });
  }

  private async proceedToken(): Promise<void> {
    this.token = new URLSearchParams(window.location.search).get('token');
    if (!this.token) {
      this.state.current = ExternalCompanyMetricsState.EMPTY;
      return;
    }
    await this.getRelatedInfo();
  }

  private async getRelatedInfo(): Promise<void> {
    try {
      this.relatedInfo = await firstValueFrom(this.mslService.getRelatedInfo(this.token));
    } catch (e) {
      this.state.current = ExternalCompanyMetricsState.EMPTY;
      return;
    }
    this.state.current = ExternalCompanyMetricsState.PASSWORD;
  }

  private updateForm(): void {
    this.form = MetricFillService.initForm(this.groupsWithAllPeriods.metricGroups);
  }

  private setTabs(): void {
    let allTabs: ExternalCompanyMetricsTabs[] = [];
    if (this.groupsWithAllPeriods.periods.actual.length) {
      allTabs.push(this.Tabs.Actual);
    }
    if (this.groupsWithAllPeriods.periods.budget.length) {
      allTabs.push(this.Tabs.Budget);
    }
    if (!allTabs.length) {
      allTabs = [this.Tabs.Actual, this.Tabs.Budget];
    }
    // allTabs.push(ExternalCompanyMetricsTabs.CompanyUpdates);
    allTabs.push(ExternalCompanyMetricsTabs.SubmitData);
    this.tabs = {
      all: allTabs,
      selected: allTabs[0],
      isActualSelected: allTabs[0] === this.Tabs.Actual,
    };
  }

  private setDataFromBulkUpload(updatedGroups: MetricGroupsAndPeriods): void {
    const groups = this.groupsWithAllPeriods.metricGroups;
    groups.forEach((group) => {
      group.metrics.forEach((metric) => {
        metric.companyMetrics[0].metricData.forEach((data) => {
          const updatedMetricData = updatedGroups
            .metricGroups.find((updatedGroup) => updatedGroup.uuid === group.uuid)
            .metrics?.find((m) => m.uuid === metric.uuid)
            .companyMetrics[0]
            .metricData?.find((updatedData) => // cannot search by uuid because ending does not have uuid.
              updatedData.role === data.role && updatedData.period === data.period);
          data.rowValue = updatedMetricData?.rowValue ?? data.rowValue;
        });
      });
    });
    this.updateForm();
  }
}
